import { formatDateOnlyLocal } from '@erp/shared-utils';
import type {
  AssignmentLine,
  GetLineStockPositionResponse,
  GetNeedInsightsResponse,
  GetSuggestionDetailResponse,
  LineStockLocation,
  NeedInventoryRow,
  NeedSalesPoint,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

/** Where the line ships from, as the Line section names it. */
/** Shown for a fact with no value, as the board's other empty cells are. */
export const NO_FACT = '\u2014';

export function formatQuantity(value: number | null): string {
  return value === null ? NO_FACT : Math.round(value).toLocaleString();
}

export type LineSource =
  | { kind: 'vendor' }
  | { kind: 'stock' }
  | {
      kind: 'transfer';
      warehouseName: string;
      /** Available to ship at the source today; null until insights load. */
      available: number | null;
    };

/**
 * The selected line's decision facts for the buyer board's Line section
 * (GEN-2944): Robert's list from the GAINS Order Builder pane, minus what the
 * row already shows (item, location, stock indicator).
 */
export interface LineFacts {
  /** Snapshot date the position is as of (`YYYY-MM-DD`), once it loads. */
  asOf: string | null;
  /** When the stock feed wrote that snapshot (ISO timestamp), once it loads. */
  asOfSyncedAt: string | null;
  position: {
    /**
     * Net available: on hand + on order − unshipped − open demand. This and
     * on hand are null until the board line or the detail loads, so a reload
     * never shows a zero that is not real.
     */
    netAvailable: number | null;
    onHand: number | null;
    unshipped: number | null;
    available: number | null;
    /** Open POs and inbound transfers; null until the stock position loads. */
    onOrder: number | null;
    transfersIn: number | null;
    /**
     * The two terms net takes off beyond unshipped, for the Net tooltip:
     * open sales order demand and staged outbound transfers. Null until the
     * stock position loads.
     */
    openSalesDemand: number | null;
    stagedOutbound: number | null;
  };
  policy: {
    /** The forecast's reorder point (GAINS Min). */
    min: number | null;
    /** The forecast's order-up-to level (GAINS Max). */
    max: number | null;
    as400Min: number | null;
    as400Max: number | null;
    /** A buyer's active min floor, and the day it lapses. */
    minFloor: number | null;
    minFloorEndsAt: string | null;
  };
  demand: {
    /** Units sold over the history window: this month and the 11 before. */
    sales12Months: number | null;
    salesMonthToDate: number | null;
    popularityCode: string | null;
    /** The destination's policy, whose extension fields show beside Pop. */
    inventoryPolicyId: string | null;
  };
  buy: {
    /** Days the line has sat pending (GEN-2940); null once it is not. */
    daysOnOrder: number | null;
    incrementQuantity: number | null;
    moqQuantity: number | null;
    leadTimeDays: number | null;
    /** The lead time is the transfer's transit, not the vendor's. */
    leadTimeIsTransit: boolean;
    source: LineSource;
  };
}

export type LineFactsDetail = Pick<
  GetSuggestionDetailResponse,
  | 'currentOnHand'
  | 'currentNetPosition'
  | 'reorderPoint'
  | 'orderUpToLevel'
  | 'policyMin'
  | 'policyMax'
  | 'options'
  | 'daysOnOrder'
>;
export type LineFactsInsights = Pick<
  GetNeedInsightsResponse,
  | 'popularityCode'
  | 'inventoryPolicyId'
  | 'inventoryByWarehouse'
  | 'salesHistory'
>;
export type LineFactsStock = Pick<
  GetLineStockPositionResponse,
  'asOf' | 'asOfSyncedAt' | 'locations'
>;
type TopOption = NonNullable<AssignmentLine['topOption']>;
/** Stock and policy readings the detail and the board line both carry. */
type Readings = Pick<
  LineFactsDetail,
  'currentOnHand' | 'currentNetPosition' | 'policyMin' | 'policyMax'
>;

function availableFrom(
  onHand: number | null,
  unshipped: number | null,
): number | null {
  return onHand === null || unshipped === null ? null : onHand - unshipped;
}

/** The live position from the stock read, the same formula as the engine. */
function livePosition(live: LineStockLocation): LineFacts['position'] {
  return {
    netAvailable: live.netAvailable,
    onHand: live.onHand,
    unshipped: live.unshipped,
    available: live.available,
    onOrder: live.onOrder,
    transfersIn: live.transfersIn,
    openSalesDemand: live.openSalesDemand,
    stagedOutbound: live.stagedOutbound,
  };
}

export function positionFacts(
  readings: Readings | null,
  destination: NeedInventoryRow | undefined,
  lineUnshipped: number | null,
  live?: LineStockLocation,
): LineFacts['position'] {
  if (live !== undefined) return livePosition(live);
  const onHand = readings?.currentOnHand ?? null;
  const unshipped = destination?.reservedQuantity ?? lineUnshipped;
  return {
    netAvailable: readings?.currentNetPosition ?? null,
    onHand,
    unshipped,
    available:
      destination?.availableQuantity ?? availableFrom(onHand, unshipped),
    onOrder: null,
    transfersIn: null,
    openSalesDemand: null,
    stagedOutbound: null,
  };
}

export function policyFacts(
  readings: Readings | null,
  detail: LineFactsDetail | undefined,
  live?: LineStockLocation,
): LineFacts['policy'] {
  return {
    min: detail?.reorderPoint ?? null,
    max: detail?.orderUpToLevel ?? null,
    as400Min: readings?.policyMin ?? null,
    as400Max: readings?.policyMax ?? null,
    minFloor: live?.minFloor ?? null,
    minFloorEndsAt: live?.minFloorEndsAt ?? null,
  };
}

/** `YYYY-MM` of the month the server buckets "now" into (UTC). */
function utcMonth(now: Date): string {
  return now.toISOString().slice(0, 7);
}

export function salesFacts(
  salesHistory: readonly NeedSalesPoint[] | undefined,
  now: Date,
): Pick<LineFacts['demand'], 'sales12Months' | 'salesMonthToDate'> {
  if (salesHistory === undefined) {
    return { sales12Months: null, salesMonthToDate: null };
  }
  const month = utcMonth(now);
  const current = salesHistory.find(
    (point) => point.monthStart.slice(0, 7) === month,
  );
  return {
    sales12Months: salesHistory.reduce((sum, point) => sum + point.quantity, 0),
    salesMonthToDate: current?.quantity ?? 0,
  };
}

function isBuyAction(action: TopOption['action']): boolean {
  return action === 'buy' || action === 'buy_and_transfer';
}

/**
 * The vendor's buy terms. A line the buyer switched to a transfer (GEN-3023)
 * has a transfer leg on top, so its terms come from the engine's lowest-ranked
 * buy option instead.
 */
export function vendorTerms(
  top: TopOption | null,
  options: LineFactsDetail['options'] | undefined,
): Pick<LineFacts['buy'], 'incrementQuantity' | 'moqQuantity'> {
  if (top !== null && top.action !== 'transfer') {
    return {
      incrementQuantity: top.vendorIncrementQuantity,
      moqQuantity: top.vendorMoqQuantity,
    };
  }
  const buy = (options ?? [])
    .filter((option) => isBuyAction(option.action))
    .sort((a, b) => a.rank - b.rank)[0];
  return {
    incrementQuantity: buy?.vendorIncrementQuantity ?? null,
    moqQuantity: buy?.vendorMoqQuantity ?? null,
  };
}

function transferSource(
  top: TopOption,
  sourceId: string,
  inventory: readonly NeedInventoryRow[] | undefined,
): LineSource {
  const row = inventory?.find((w) => w.warehouseId === sourceId);
  return {
    kind: 'transfer',
    warehouseName: top.sourceWarehouseName ?? row?.warehouseName ?? sourceId,
    available: row?.availableQuantity ?? null,
  };
}

export function lineSource(
  top: TopOption | null,
  inventory: readonly NeedInventoryRow[] | undefined,
): LineSource {
  if (top?.action === 'stock') return { kind: 'stock' };
  if (top?.action !== 'transfer' || top.sourceWarehouseId == null) {
    return { kind: 'vendor' };
  }
  return transferSource(top, top.sourceWarehouseId, inventory);
}

export function buyFacts(
  top: TopOption | null,
  detail: LineFactsDetail | undefined,
  insights: LineFactsInsights | undefined,
): LineFacts['buy'] {
  return {
    daysOnOrder: detail?.daysOnOrder ?? null,
    ...vendorTerms(top, detail?.options),
    leadTimeDays:
      top?.leadTimeOverrideDays ?? top?.estimatedLeadTimeDays ?? null,
    leadTimeIsTransit: top?.action === 'transfer',
    source: lineSource(top, insights?.inventoryByWarehouse),
  };
}

/**
 * The destination row, when it measures the position: the destination has a
 * stock snapshot (`asOf`) or inbound supply. A row read only for its policy
 * carries zeros for stock it never saw; a missing snapshot is unknown, not
 * zero, so the engine run's position stays.
 */
export function measuredPosition(
  asOf: string | null,
  row: LineStockLocation | undefined,
): LineStockLocation | undefined {
  if (row === undefined) return undefined;
  const hasSupply = row.onOrder > 0 || row.transfersIn > 0;
  return asOf !== null || hasSupply ? row : undefined;
}

/**
 * Builds the Line section's facts from the board line, the planning-details
 * read and the insights read. Any of the three can still be loading: the
 * board line always is in memory, the reads fill in as they arrive.
 */
export function lineFacts({
  line,
  detail,
  insights,
  stock,
  now = new Date(),
}: {
  line: AssignmentLine | null;
  detail: LineFactsDetail | undefined;
  insights: LineFactsInsights | undefined;
  stock?: LineFactsStock;
  now?: Date;
}): LineFacts {
  const readings: Readings | null = detail ?? line;
  const destination = insights?.inventoryByWarehouse.find(
    (w) => w.isDestination,
  );
  const destinationRow = stock?.locations.find(
    (location) => location.isDestination,
  );
  const top = line?.topOption ?? null;
  return {
    asOf: stock?.asOf ?? null,
    asOfSyncedAt: stock?.asOfSyncedAt ?? null,
    position: positionFacts(
      readings,
      destination,
      line?.reservedQuantity ?? null,
      measuredPosition(stock?.asOf ?? null, destinationRow),
    ),
    policy: policyFacts(readings, detail, destinationRow),
    demand: {
      ...salesFacts(insights?.salesHistory, now),
      popularityCode: insights?.popularityCode ?? line?.popularityCode ?? null,
      inventoryPolicyId: insights?.inventoryPolicyId ?? null,
    },
    buy: buyFacts(top, detail, insights),
  };
}

/**
 * Open sales orders beyond what unshipped already holds: net takes off the
 * larger of unshipped and open SO demand, so only this excess is extra.
 */
export function salesBeyondUnshipped(position: LineFacts['position']): number {
  return Math.max(
    (position.openSalesDemand ?? 0) - (position.unshipped ?? 0),
    0,
  );
}

/**
 * Whether net takes off more than unshipped, so the five stock rows alone do
 * not add up (net ≠ available + on order) and the Net row needs its tooltip.
 */
export function netHasExtraTerms(position: LineFacts['position']): boolean {
  return (
    salesBeyondUnshipped(position) !== 0 || (position.stagedOutbound ?? 0) !== 0
  );
}

/**
 * On order as one figure: open POs plus inbound transfers, the way the
 * buyer's stock screen sums them. Null until either part is known.
 */
export function totalOnOrder(position: LineFacts['position']): number | null {
  if (position.onOrder === null && position.transfersIn === null) return null;
  return (position.onOrder ?? 0) + (position.transfersIn ?? 0);
}

/** Lead time in weeks, as GAINS shows it (one decimal). */
export function leadTimeWeeks(days: number): number {
  return Math.round((days / 7) * 10) / 10;
}

/**
 * Whether the stock feed wrote the snapshot on its own date (browser local),
 * so "as of" can carry the write time. A late write of an earlier snapshot
 * must not read as that later day's stock.
 */
export function syncedOnSnapshotDay(asOf: string, syncedAt: string): boolean {
  return formatDateOnlyLocal(new Date(syncedAt)) === asOf;
}
